import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Star, Eye, MessageCircle, ArrowLeft, AlertTriangle, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, media } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { fmtPrice, vTitle } from "@/lib/format";
import { Loader, Section } from "@/components/xd/ui";
import { NameForm, splitName } from "@/components/xd/NameForm";
import { refLink, CopyButton } from "@/pages/broker/Broker";

const HomeStat = ({ icon: Icon, label, value, testid, note }) => (
  <div className="xd-panel p-4 md:p-5 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-2 text-zinc-400"><Icon size={15} className="shrink-0" /><p className="text-xs leading-snug">{label}</p></div>
    <p className="mt-2 text-2xl md:text-3xl font-semibold chrome-text ltr-num" data-testid={`${testid}-value`}>{value}</p>
    {note && <p className="mt-1.5 text-[10px] leading-snug text-zinc-600">{note}</p>}
  </div>
);

const LatestCard = ({ v }) => {
  const { t, lang } = useLang();
  const cover = v.photos?.[0];
  return (
    <Link to={`/broker/v/${v.code}`} className="xd-panel flex lg:flex-col overflow-hidden group hover:border-white/25 transition-colors" data-testid={`home-latest-${v.code}`}>
      <div className="w-32 sm:w-44 lg:w-full shrink-0 aspect-[4/3] bg-zinc-900 overflow-hidden">
        {cover && <img src={media(cover.id, "thumb")} alt={vTitle(v, lang)} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      </div>
      <div className="p-3 lg:p-4 min-w-0 flex-1 space-y-1">
        <p className="font-medium text-sm line-clamp-2 break-words">{v.brand} {v.model}</p>
        <p className="text-xs text-zinc-500"><span className="ltr-num">{v.year} · {v.code}</span></p>
        <p className="text-sm chrome-text font-semibold"><span className="ltr-num">{fmtPrice(v.price)}</span></p>
        <div><span className={`xd-badge xd-badge-${v.availability} inline-block`} data-testid={`home-latest-status-${v.code}`}>{t(`status.${v.availability}`)}</span></div>
      </div>
    </Link>
  );
};

const ProfileBox = ({ open, setOpen }) => {
  const { t, tErr } = useLang();
  const { user, setUser } = useAuth();
  const save = async (body) => {
    try { const { data } = await api.put("/broker/profile", body); setUser(data.user); toast.success(t("profile.name_saved")); setOpen(false); }
    catch (e) { toast.error(tErr(e)); }
  };
  return (
    <Section title={t("profile.my")} testid="home-profile" actions={!open && <button className="btn-ghost !min-h-[2.5rem] text-sm" onClick={() => setOpen(true)} data-testid="home-profile-edit"><UserRound size={15} />{t("profile.edit_name")}</button>}>
      <div className="text-sm text-zinc-300 space-y-1">
        <p data-testid="home-profile-name">{user.name || "—"}</p>
        <p className="text-xs text-zinc-500 ltr-num">{user.uid} · {user.phone}</p>
      </div>
      {open && <NameForm user={user} onSave={save} testid="home-profile-form" />}
      <p className="text-[11px] text-zinc-600">{t("profile.name_hint")}</p>
    </Section>
  );
};

export default function BrokerHome() {
  const { t } = useLang();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [editName, setEditName] = useState(false);
  useEffect(() => { api.get("/broker/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  const first = splitName(user)[0];
  const openProfile = () => { setEditName(true); setTimeout(() => document.querySelector("[data-testid='home-profile']")?.scrollIntoView({ behavior: "smooth" }), 50); };
  return (
    <div className="space-y-6 md:space-y-8" data-testid="broker-home-page">
      <div className="xd-panel p-5 md:p-7 border-s-2 border-s-[var(--xd-red)]" data-testid="home-welcome">
        <p className="eyebrow">{t("broker.portal")} · <span className="ltr-num">{user.uid}</span></p>
        <h1 className="mt-3 text-2xl sm:text-3xl md:text-4xl font-semibold chrome-text leading-tight break-words" data-testid="home-greeting">
          {first ? t("home.welcome", { name: first }) : t("home.welcome_generic")}
        </h1>
        {first ? <p className="mt-2 text-sm md:text-base text-zinc-400" data-testid="home-subtitle">{t("home.subtitle")}</p>
          : <button type="button" onClick={openProfile} className="mt-2 min-h-[2.5rem] text-start text-sm md:text-base text-amber-200 underline underline-offset-4" data-testid="home-subtitle">{t("home.complete_profile")}</button>}
      </div>
      {d === null ? <Loader /> : d === false ? <p className="text-sm text-red-400" data-testid="home-error">{t("err.generic")}</p> : (
        <>
          {d.favorites_unavailable > 0 && (
            <Link to="/broker/favorites" className="flex items-start gap-3 rounded-lg border border-amber-400/30 bg-amber-400/5 px-4 py-3 text-sm text-amber-200 hover:bg-amber-400/10 transition-colors" data-testid="home-fav-notice">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{t("home.fav_notice")} <span className="underline underline-offset-4">{t("home.fav_notice_link")}</span></span>
            </Link>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <HomeStat icon={Car} label={t("home.stat_publishable")} value={d.publishable} testid="home-stat-publishable" />
            <HomeStat icon={Star} label={t("home.stat_favorites")} value={d.favorites} testid="home-stat-favorites" />
            <HomeStat icon={Eye} label={t("home.stat_visits")} value={d.referral_visits} testid="home-stat-visits" />
            <HomeStat icon={MessageCircle} label={t("home.stat_whatsapp")} value={d.whatsapp_clicks} testid="home-stat-whatsapp" note={t("home.whatsapp_note")} />
          </div>
          <div className="grid lg:grid-cols-3 gap-3" data-testid="home-actions">
            <Link to="/broker/vehicles" className="btn-xd w-full justify-center" data-testid="home-action-vehicles"><Car size={16} />{t("home.browse")}</Link>
            <Link to="/broker/favorites" className="btn-ghost w-full justify-center" data-testid="home-action-favorites"><Star size={16} />{t("home.open_favorites")}</Link>
            <CopyButton text={refLink(user.uid)} label={t("home.copy_ref")} testid="home-action-copy-ref" className="w-full justify-center" />
          </div>
          <Section title={t("home.latest")} testid="home-latest"
            actions={<Link to="/broker/vehicles" className="min-h-[2.5rem] text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="home-view-all">{t("bhome.view_all")}<ArrowLeft size={14} className="ltr:rotate-180" /></Link>}>
            {d.latest.length ? <div className="grid gap-3 lg:grid-cols-3">{d.latest.map((v) => <LatestCard key={v.id} v={v} />)}</div>
              : <p className="text-sm text-zinc-500">{t("home.latest_empty")}</p>}
          </Section>
        </>
      )}
      <ProfileBox open={editName} setOpen={setEditName} />
    </div>
  );
}
